// Type-only, so this file doesn't pull the SDK into the main bundle; it is loaded below when needed.
import type { Faro } from '@grafana/faro-web-sdk';
import type { AnyRouter } from '@tanstack/react-router';

import { getClientConfigForEnv } from '@/client_config';
import type { ParsedEnvVariables } from '@/stores/config/config.type.ts';
import { useUserSettingsStore } from '@/stores/userSettings.ts';
import { isLocalhost } from '@/utils/api.ts';

let started = false;
let faro: Faro | undefined;
let userId: string | undefined;
let page: string | undefined;

/**
 * Starts browser reports (Grafana Faro) to the cloud receiver for this environment, from edge and
 * cloud alike. Local dev servers don't report. See docs/configuration.md.
 */
export const initFrontendLogs = async ({ imageTag, env, clusterName }: ParsedEnvVariables) => {
  const { faroUrl } = getClientConfigForEnv(env);
  if (started || !faroUrl || isLocalhost()) return;
  started = true;
  const sdk = await import('@grafana/faro-web-sdk').catch(() => undefined);
  if (!sdk) return;
  const {
    ConsoleInstrumentation,
    ErrorsInstrumentation,
    initializeFaro,
    LogLevel,
    SessionInstrumentation,
    ViewInstrumentation,
  } = sdk;
  faro = initializeFaro({
    url: faroUrl,
    app: { name: 'sitecontrol-web', version: imageTag, environment: env, namespace: clusterName },
    user: userId ? { id: userId } : undefined,
    view: page ? { name: page } : undefined,
    sessionTracking: { session: { attributes: themeAttributes() } },
    instrumentations: [
      new ErrorsInstrumentation(),
      new ConsoleInstrumentation(),
      new SessionInstrumentation(),
      new ViewInstrumentation(),
    ],
    consoleInstrumentation: {
      disabledLevels: [LogLevel.TRACE, LogLevel.DEBUG, LogLevel.INFO, LogLevel.LOG, LogLevel.WARN],
    },
  });
  // A reloaded tab resumes its stored session, whose attributes win over the ones above.
  updateThemeAttributes();
  useUserSettingsStore.subscribe(updateThemeAttributes);
  darkModeQuery().addEventListener('change', updateThemeAttributes);
};

/** The login can finish before Faro starts, so the id is kept and applied on start too. */
export const setFrontendLogsUser = (id: string | undefined) => {
  userId = id;
  if (id) faro?.api.setUser({ id });
  else faro?.api.resetUser();
};

/**
 * Reports each page change for usage counts. A page is named by its route, e.g. `/layouts/$id`,
 * so pages group without their ids. Kept like the user id, as navigation can come first.
 */
export const trackFrontendLogsPages = (router: AnyRouter) =>
  router.subscribe('onResolved', () => {
    const { matches } = router.state;
    // An address with no route would otherwise count as the root page.
    const notFound = matches.some(match => match.status === 'notFound' || match._notFound);
    const name = notFound ? 'not-found' : matches.at(-1)?.fullPath;
    if (!name) return;
    page = name;
    faro?.api.setView({ name });
  });

const darkModeQuery = () => window.matchMedia('(prefers-color-scheme: dark)');

/** The theme setting and the theme it shows, resolving auto the way ScaleuiWrapper does. */
const themeAttributes = () => {
  const { theme } = useUserSettingsStore.getState();
  const shown = theme === 'auto' ? (darkModeQuery().matches ? 'dark' : 'light') : theme;
  return { theme: shown, theme_setting: theme };
};

const updateThemeAttributes = () => {
  const session = faro?.api.getSession();
  if (!faro || !session) return;
  const { theme, theme_setting } = themeAttributes();
  if (theme === session.attributes?.theme && theme_setting === session.attributes?.theme_setting)
    return;
  faro.api.setSession({ ...session, attributes: { ...session.attributes, theme, theme_setting } });
};
